import { useEffect, useMemo, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import PageHeader from "@/components/PageHeader";
import ProgressPhotos from "@/pages/ProgressPhotos";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { fromCm, fromKg, lengthUnit, toCm, toKg, weightUnit } from "@/lib/locale";

const FIELDS = [
  { id: "weight", label: "Weight", unit: "kg" },
  { id: "body_fat", label: "Body Fat", unit: "%" },
  { id: "chest", label: "Chest", unit: "cm" },
  { id: "waist", label: "Waist", unit: "cm" },
  { id: "hips", label: "Hips", unit: "cm" },
  { id: "arms", label: "Arms", unit: "cm" },
];

export function Sparkline({ data }) {
  const pts = data.map((d) => d.weight).filter((v) => typeof v === "number");
  if (pts.length < 2) return <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)", fontSize: 13.5 }}>Log at least two weight entries to see your trend.</div>;
  const W = 520, H = 150, pad = 16;
  const min = Math.min(...pts) - 1, max = Math.max(...pts) + 1;
  const sx = (i) => pad + (i / (pts.length - 1)) * (W - pad * 2);
  const sy = (v) => H - pad - ((v - min) / (max - min || 1)) * (H - pad * 2);
  const line = pts.map((v, i) => `${i === 0 ? "M" : "L"}${sx(i)},${sy(v)}`).join(" ");
  const area = `${line} L${sx(pts.length - 1)},${H} L${sx(0)},${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 150 }}>
      <defs><linearGradient id="wg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="rgba(12,12,14,0.08)" /><stop offset="100%" stopColor="rgba(12,12,14,0)" /></linearGradient></defs>
      <path d={area} fill="url(#wg)" />
      <path d={line} fill="none" stroke="var(--ink)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((v, i) => <circle key={`${i}-${v}`} cx={sx(i)} cy={sy(v)} r="3" fill="var(--ink)" />)}
    </svg>
  );
}

// Stored in kg / cm; shown and entered in the person's own units (lb / in for imperial).
const KIND = { weight: "w", chest: "l", waist: "l", hips: "l", arms: "l" };
const shownValue = (id, v, user) => (v == null ? v : KIND[id] === "w" ? fromKg(v, user) : KIND[id] === "l" ? fromCm(v, user) : v);
const storedValue = (id, v, user) => (KIND[id] === "w" ? toKg(v, user) : KIND[id] === "l" ? toCm(v, user) : v);

export default function Progress() {
  const { push } = useToast();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("measurements");

  const load = useCallback(() => api.get("/progress").then((r) => setEntries(r.data)).catch(() => {}), []);
  const fields = useMemo(() => FIELDS.map((f) => ({ ...f, unit: f.unit === "kg" ? weightUnit(user) : f.unit === "cm" ? lengthUnit(user) : f.unit })), [user]);
  const shown = useMemo(() => entries.map((e) => ({ ...e, ...Object.fromEntries(FIELDS.map((f) => [f.id, shownValue(f.id, e[f.id], user)])) })), [entries, user]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = {};
    let has = false;
    FIELDS.forEach((f) => { const v = parseFloat(form[f.id]); if (!isNaN(v)) { payload[f.id] = storedValue(f.id, v, user); has = true; } });
    if (!has) { push("Enter at least one value.", "error"); return; }
    setSaving(true);
    try {
      await api.post("/progress", payload);
      push("Progress saved.", "success");
      setOpen(false); setForm({});
      load();
    } catch { push("Could not save.", "error"); } finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/progress/${id}`).catch(() => {}); push("Entry deleted."); load(); };

  const latest = shown[shown.length - 1] || {};
  const prev = shown[shown.length - 2] || {};

  return (
    <div>
      <PageHeader eyebrow="Progress Tracker" title="Body Progress" subtitle="Log measurements over time and watch your trend take shape."
        action={tab === "measurements" ? <button className="btn btn-primary" data-testid="log-progress-btn" onClick={() => setOpen(true)}><Icons.Plus size={18} /> Log today</button> : null} />

      <div className="tabs">
        {[["measurements", "Measurements", "Ruler"], ["photos", "Photos", "Images"]].map(([id, label, icon]) => {
          const Icon = Icons[icon] || Icons.Circle;
          const active = tab === id;
          return (
            <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)} className={`tab${active ? " active" : ""}`}>
              <Icon size={16} /> {label}
            </button>
          );
        })}
      </div>

      {tab === "photos" && <ProgressPhotos />}

      {tab === "measurements" && (<>
      <div className="grid-stats" style={{ marginBottom: 16 }}>
        {["weight", "body_fat", "waist", "chest"].map((fid, i) => {
          const f = fields.find((x) => x.id === fid);
          const val = latest[fid], pv = prev[fid];
          const diff = (typeof val === "number" && typeof pv === "number") ? Math.round((val - pv) * 10) / 10 : null;
          const better = diff !== null && (["weight", "body_fat", "waist"].includes(fid) ? diff < 0 : diff > 0);
          return (
            <div key={fid} className="clay fade-up stat-card" style={{ padding: 20, animationDelay: `${i * 60}ms` }}>
              <div style={{ fontSize: 12.5, color: "var(--text-3)", fontWeight: 600, marginBottom: 8 }}>{f.label}</div>
              <div className="display" style={{ fontSize: 26, fontWeight: 600 }}>{val ?? "—"}<span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-2)" }}> {val != null ? f.unit : ""}</span></div>
              {diff !== null ? (
                <div style={{ fontSize: 12, marginTop: 6, color: better ? "var(--teal)" : "var(--accent)", display: "flex", alignItems: "center", gap: 3 }}>
                  {diff < 0 ? <Icons.ArrowDown size={13} /> : <Icons.ArrowUp size={13} />} {Math.abs(diff)} {f.unit} vs last
                </div>
              ) : <div style={{ fontSize: 12, marginTop: 6, color: "var(--text-3)" }}>{val != null ? "First entry" : "No data yet"}</div>}
            </div>
          );
        })}
      </div>

      <div className="grid-main-side">
        <div className="clay fade-up" style={{ padding: 26 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Trend</div>
          <h3 className="display" style={{ fontSize: 17, fontWeight: 600, marginBottom: 16 }}>Weight over time</h3>
          <Sparkline data={shown} />
        </div>
        <div className="clay fade-up" style={{ padding: 26, animationDelay: "80ms" }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>Latest measurements</div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {fields.map((f) => (
              <div key={f.id} style={{ display: "flex", justifyContent: "space-between", padding: "11px 0", borderBottom: "1px solid rgba(139,150,172,0.16)" }}>
                <span style={{ fontSize: 13.5, color: "var(--text-2)" }}>{f.label}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{latest[f.id] != null ? `${latest[f.id]} ${f.unit}` : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="clay fade-up" style={{ padding: 26, marginTop: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div className="eyebrow">History</div>
          <span className="chip chip-neutral">{entries.length} entries</span>
        </div>
        {entries.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-3)", fontSize: 14 }}>No entries yet. Log your first measurement.</div>
        ) : (
          <div data-testid="progress-list">
            {[...shown].reverse().map((e) => (
              <div key={e.id} className="clay-inset history-row">
                <span className="history-date">{new Date(e.date + "T12:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                <span className="history-vals">
                  {[["weight", weightUnit(user)], ["body_fat", "%"], ["waist", `${lengthUnit(user)} waist`], ["chest", `${lengthUnit(user)} chest`]].filter(([k]) => e[k] != null).map(([k, u]) => (
                    <span key={k} className="chip chip-neutral">{e[k]} {u}</span>
                  ))}
                </span>
                <button className="icon-btn" data-testid={`delete-progress-${e.id}`} onClick={() => remove(e.id)} aria-label="Delete entry"><Icons.Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      </>)}

      {open && tab === "measurements" && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(27,33,48,0.45)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} className="glass fade-up" style={{ width: "100%", maxWidth: 460, padding: 30 }} data-testid="progress-modal">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 className="display" style={{ fontSize: 20, fontWeight: 600 }}>Log measurements</h3>
              <Icons.X size={20} style={{ cursor: "pointer", color: "var(--text-3)" }} onClick={() => setOpen(false)} />
            </div>
            <div className="grid-pair">
              {fields.map((f) => (
                <div key={f.id}>
                  <label className="label">{f.label} ({f.unit})</label>
                  <input type="number" step="0.1" className="field" data-testid={`input-${f.id}`} value={form[f.id] || ""} onChange={(e) => setForm({ ...form, [f.id]: e.target.value })} placeholder={f.unit} />
                </div>
              ))}
            </div>
            <button className="btn btn-primary" data-testid="save-progress-btn" disabled={saving} onClick={save} style={{ width: "100%", marginTop: 22, padding: 14 }}>{saving ? "Saving..." : "Save entry"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
